// Gestor centralizado del ciclo de actualización del SW.
//
// Sustituye al antiguo banner manual: detecta cuándo hay una versión nueva
// y la aplica automáticamente, salvo que:
//   - una pantalla declare bloqueo (BlockAutoUpdate) — caso típico: edición
//     de un detalle con cambios sin guardar / fotos en el formulario,
//   - haya cola offline pendiente o un flush en curso — no queremos reload
//     en medio de una subida multipart con foto.
//
// Cuando los bloqueos desaparecen (usuario sale de edit, cola se vacía),
// reintenta automáticamente. También expone `checkForUpdate()` para el
// botón "Buscar actualizaciones" de Ajustes.

import { track } from './analytics';
import { telemetryLog } from './telemetry';
import { getOfflineSyncSnapshot, subscribeOfflineSync } from '../hooks/useOfflineSync';

// 'stuck': se pidió la recarga final y el navegador no la hizo (visto en iOS,
// sep 2026). El toast deja de girar y pide cerrar y reabrir la app.
export type UpdateStatus = 'idle' | 'available' | 'applying' | 'stuck';

export interface UpdateState {
  status: UpdateStatus;
  hasUpdate: boolean;
  blockedReasons: string[];
  lastCheckAt: number | null;
}

const blocks = new Set<string>();
const listeners = new Set<(s: UpdateState) => void>();

// ── Cortacircuitos de recargas ─────────────────────────────────────────────
// Escenario de campo: arranque con cola offline grande → el SW viejo lanza
// un flush con waitUntil que dura minutos → llega una versión nueva → se
// manda SKIP_WAITING pero el SW nuevo NO puede activarse mientras el viejo
// tenga eventos en vuelo → controllerchange no llega → el salvavidas
// recarga → la página arranca otra vez bajo el SW viejo (y relanza el
// flush) → vuelve a detectar la update → bucle de toast + recarga hasta
// que la cola termina. Un despliegue a medio propagar en el CDN produce el
// mismo síntoma. Tras N intentos de aplicar el MISMO build en ventana
// corta, dejamos de recargar: la update queda en 'available' y la recarga
// definitiva la hace el listener global de controllerchange cuando el SW
// nuevo por fin tome el control (o el siguiente arranque).
const APPLY_ATTEMPTS_KEY = 'updateManager.applyAttempts.v1';
const APPLY_ATTEMPTS_MAX = 3;
const APPLY_ATTEMPTS_WINDOW_MS = 10 * 60 * 1000;

interface ApplyAttempts { build: number; count: number; lastAt: number; }

function readApplyAttempts(): ApplyAttempts | null {
  try {
    const raw = sessionStorage.getItem(APPLY_ATTEMPTS_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as ApplyAttempts;
    if (typeof v.build !== 'number' || typeof v.count !== 'number' || typeof v.lastAt !== 'number') {
      return null;
    }
    return v;
  } catch { return null; }
}

// Telemetría del bucle: una sola vez por sesión (cada recarga del bucle es
// una sesión nueva, así que en la práctica se emite al viaje que activa el
// cortacircuitos).
let loopTracked = false;

function isApplyCircuitOpen(): boolean {
  const a = readApplyAttempts();
  if (!a) return false;
  // Un build distinto al de esta página = la update aterrizó (o hay otra
  // nueva): tanda de intentos nueva.
  if (a.build !== __BUILD_TIME__) return false;
  if (a.count < APPLY_ATTEMPTS_MAX) return false;
  if (Date.now() - a.lastAt >= APPLY_ATTEMPTS_WINDOW_MS) return false;
  if (!loopTracked) {
    loopTracked = true;
    track({ name: 'app_update_loop_detected', params: { attempts: a.count } });
    traceUpdateStep('cortacircuitos', { intentos: a.count });
  }
  return true;
}

function bumpApplyAttempts(): void {
  try {
    const prev = readApplyAttempts();
    const next: ApplyAttempts = prev && prev.build === __BUILD_TIME__
      ? { build: prev.build, count: prev.count + 1, lastAt: Date.now() }
      : { build: __BUILD_TIME__, count: 1, lastAt: Date.now() };
    sessionStorage.setItem(APPLY_ATTEMPTS_KEY, JSON.stringify(next));
  } catch { /* sessionStorage no disponible — sin cortacircuitos */ }
}

function clearApplyAttempts(): void {
  try { sessionStorage.removeItem(APPLY_ATTEMPTS_KEY); } catch { /* nada */ }
}

// ── Rastro del ciclo de actualización (telemetría) ─────────────────────────
// 29 sep 2026: un iPhone se quedó minutos en "Actualizando…" y no había forma
// de saber en qué paso: track() va a GA4 y la telemetría no registraba nada.
// Cada paso se manda a la telemetría (evento 'actualizacion') y además se
// apunta en localStorage, porque lo que ocurre justo antes de recargar suele
// perderse con la página. Al arrancar, el rastro de la sesión anterior se
// cierra con un único evento: 'completada' (la recarga llegó a la versión
// nueva, con el tiempo total) o 'no_completada' (se volvió a arrancar en la
// misma versión: la mataron, o la recarga nunca ocurrió).
const TRACE_KEY = 'updateManager.trace.v1';
const TRACE_MAX_STEPS = 30;

// Estado de los service workers en una cadena corta para el rastro:
// c = el que controla esta página; a/w/i = activo/esperando/instalando de la
// registración. Ej.: "c:act a:aing w:- i:-" = la página sigue con el viejo y el
// nuevo lleva un rato en 'activating' (la sospecha en iOS, sep 2026: ~70 s con
// todas las peticiones fallando al instante durante el relevo).
const SW_STATE_SHORT: Record<string, string> = {
  parsed: 'par', installing: 'ing', installed: 'ied', activating: 'aing', activated: 'act', redundant: 'red',
};

type SwLike = { state: string } | null | undefined;

export function describeSwState(
  controller: SwLike,
  reg: { active?: SwLike; waiting?: SwLike; installing?: SwLike } | null | undefined,
): string {
  const s = (w: SwLike) => (w ? (SW_STATE_SHORT[w.state] ?? w.state) : '-');
  return `c:${s(controller)} a:${s(reg?.active)} w:${s(reg?.waiting)} i:${s(reg?.installing)}`;
}

function currentSwState(): string {
  try {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return 'sin-sw';
    return describeSwState(navigator.serviceWorker.controller, registration);
  } catch { return '?'; }
}

export interface UpdateTrace {
  desde: string;   // versión de la página que empezó la actualización
  build: number;   // su __BUILD_TIME__
  inicio: number;  // ms epoch del primer paso
  pasos: string[]; // "paso+segundos", p. ej. "skip_waiting+0.4s"
}

function readTrace(): UpdateTrace | null {
  try {
    const raw = localStorage.getItem(TRACE_KEY);
    if (!raw) return null;
    const t = JSON.parse(raw) as UpdateTrace;
    if (typeof t.build !== 'number' || typeof t.inicio !== 'number' || !Array.isArray(t.pasos)) return null;
    return t;
  } catch { return null; }
}

function writeTrace(t: UpdateTrace | null): void {
  try {
    if (t) localStorage.setItem(TRACE_KEY, JSON.stringify(t));
    else localStorage.removeItem(TRACE_KEY);
  } catch { /* sin localStorage: solo queda el evento en vivo */ }
}

export function traceUpdateStep(paso: string, extra: Record<string, unknown> = {}, now: number = Date.now()): void {
  let t = readTrace();
  if (!t || t.build !== __BUILD_TIME__) {
    t = { desde: __APP_VERSION__, build: __BUILD_TIME__, inicio: now, pasos: [] };
  }
  const s = Math.round((now - t.inicio) / 100) / 10;
  const sw = currentSwState();
  t.pasos = [...t.pasos, `${paso}+${s}s[${sw}]`].slice(-TRACE_MAX_STEPS);
  writeTrace(t);
  telemetryLog('actualizacion', { paso, desde: __APP_VERSION__, s, sw, ...extra });
}

// Cierre del rastro de la sesión anterior. Pura para poder probarla.
export function closeTraceOnBoot(
  trace: UpdateTrace | null,
  currentBuild: number,
  currentVersion: string,
  now: number,
): Record<string, unknown> | null {
  if (!trace) return null;
  const s = Math.round((now - trace.inicio) / 100) / 10;
  const pasos = trace.pasos.join(' ');
  if (trace.build !== currentBuild) {
    return { paso: 'completada', desde: trace.desde, a: currentVersion, s, pasos };
  }
  return { paso: 'no_completada', desde: trace.desde, s, pasos };
}

function reportPreviousTrace(): void {
  const cierre = closeTraceOnBoot(readTrace(), __BUILD_TIME__, __APP_VERSION__, Date.now());
  writeTrace(null);
  if (cierre) telemetryLog('actualizacion', cierre);
}

let registration: ServiceWorkerRegistration | null = null;
let hasUpdate = false;
let status: UpdateStatus = 'idle';
let lastCheckAt: number | null = null;
let promptTracked = false;
let initialized = false;
let applying = false;

function snapshot(): UpdateState {
  return {
    status,
    hasUpdate,
    blockedReasons: [...blocks],
    lastCheckAt,
  };
}

function emit() {
  const s = snapshot();
  listeners.forEach((l) => l(s));
}

export function subscribeUpdate(cb: (s: UpdateState) => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

export function getUpdateSnapshot(): UpdateState {
  return snapshot();
}

// ── Recarga vigilada ───────────────────────────────────────────────────────
// En iOS (PWA instalada) se vio `location.reload()` no hacer nada durante el
// relevo de SW: la página vieja seguía viva, sus peticiones fallaban al
// instante y el toast "Actualizando…" no se iba nunca — hubo que matar la
// app. Si tras RELOAD_WATCHDOG_MS seguimos aquí, se reintenta con
// location.replace; si tampoco, el toast pasa a 'stuck' y pide cerrar y
// reabrir (con botón de reintento). Si la recarga funciona, la página se va y
// estos temporizadores mueren con ella.
export const RELOAD_WATCHDOG_MS = 6000;
// Muestreo del estado de los SW mientras dura el relevo.
const UPDATE_SAMPLE_EVERY_MS = 10000;
const UPDATE_SAMPLE_MAX_MS = 120000;

export function reloadWithWatchdog(): void {
  traceUpdateStep('recarga_pedida');
  window.location.reload();
  setTimeout(() => {
    traceUpdateStep('recarga_reintento');
    window.location.replace(window.location.href);
    setTimeout(() => {
      traceUpdateStep('recarga_fallida');
      status = 'stuck';
      emit();
    }, RELOAD_WATCHDOG_MS);
  }, RELOAD_WATCHDOG_MS);
}

// Botón "Reintentar" del toast atascado.
export function retryStuckReload(): void {
  traceUpdateStep('reintento_usuario');
  window.location.reload();
}

export function blockAutoUpdate(reason: string) {
  blocks.add(reason);
  emit();
}

export function unblockAutoUpdate(reason: string) {
  blocks.delete(reason);
  emit();
  // Al liberar el último bloqueo, intentamos aplicar si hay update lista.
  if (blocks.size === 0) void tryApplyIfReady();
}

function isSafeToApply(): boolean {
  if (blocks.size > 0) return false;
  // Bloqueamos SOLO durante un flush activo (segundos). NO bloqueamos
  // por pendingCount > 0: si justo el bug está en la sincronización,
  // bloquear por "tienes pendientes" deja al usuario atrapado en la
  // versión rota sin posibilidad de recibir el fix. La cola vive en
  // IndexedDB, persiste al reemplazar el SW, y el nuevo SW retoma el
  // background sync con el código corregido. Worst case: una foto
  // multipart en vuelo se reintenta y se deduplica server-side.
  const { isFlushing } = getOfflineSyncSnapshot();
  if (isFlushing) return false;
  return true;
}

async function tryApplyIfReady() {
  if (applying) return;
  if (!hasUpdate) return;
  // En el camino SW normal pedimos que haya waiting; en el fallback
  // (LAN-HTTP sin SW) basta con que hasUpdate esté marcado.
  const hasWaiting = !!registration?.waiting;
  const swMissing = !('serviceWorker' in navigator);
  if (!hasWaiting && !swMissing) return;
  if (!isSafeToApply()) return;
  applyUpdate();
}

function applyUpdate() {
  if (applying) return;
  // Cortacircuitos: mismo build, 3+ intentos recientes → no aplicamos.
  // El estado queda en 'available' (sin toast ni reload); la recarga
  // definitiva llegará vía el listener global de controllerchange o en el
  // siguiente arranque.
  if (isApplyCircuitOpen()) return;
  applying = true;
  bumpApplyAttempts();
  status = 'applying';
  emit();
  track({ name: 'app_update_applied' });
  traceUpdateStep('aplicando', { conSW: !!registration?.waiting });
  let reloaded = false;
  const reload = (delayMs = 0) => {
    if (reloaded) return;
    reloaded = true;
    if (delayMs > 0) setTimeout(reloadWithWatchdog, delayMs);
    else reloadWithWatchdog();
  };
  if (registration?.waiting && 'serviceWorker' in navigator) {
    // Camino normal: hay un SW nuevo esperando. Le mandamos skipWaiting
    // y recargamos EN CUANTO toma el control, sin esperas.
    //
    // El retardo que había aquí para lucir el toast abría una ventana en la
    // que la página VIEJA seguía viva ya controlada por el SW NUEVO, que en
    // su activate borra del precache los assets del build anterior (y en
    // Azure Static Web Apps tampoco siguen en el servidor: cada despliegue
    // reemplaza el contenido). Cualquier recurso pedido en ese hueco ya no
    // existía en ningún sitio. El toast se ve igual mientras dura
    // skipWaiting → activate; no hace falta alargarlo a costa de eso.
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      traceUpdateStep('relevo');
      reload();
    }, { once: true });
    // Salvavidas por si controllerchange no llega (algunos navegadores lo
    // omiten cuando la app no tenía controller previo).
    setTimeout(() => {
      // Si el SW nuevo SIGUE esperando, el viejo aún manda: recargar aquí
      // sería a ciegas y podría pillar la activación a medias, que es justo
      // lo que dejaba la pantalla en blanco. Devolvemos el estado a
      // 'available' y que sea el listener global de controllerchange quien
      // recargue cuando el relevo se complete de verdad.
      if (registration?.waiting) {
        traceUpdateStep('sin_relevo');
        applying = false;
        status = 'available';
        emit();
        return;
      }
      if (!reloaded) traceUpdateStep('relevo_sin_evento');
      reload();
    }, 8000);
    // Diagnóstico del relevo (iOS): cada cambio de estado del SW nuevo y del
    // que controla la página, y una muestra cada 10 s mientras se espera. Si la
    // recarga ocurre, la página se va y todo esto muere con ella.
    const nuevo = registration.waiting;
    nuevo.addEventListener('statechange', () => traceUpdateStep(`sw_nuevo_${nuevo.state}`));
    const viejo = navigator.serviceWorker.controller;
    viejo?.addEventListener('statechange', () => traceUpdateStep(`sw_viejo_${viejo.state}`));
    const esperaDesde = Date.now();
    const muestreo = setInterval(() => {
      if (Date.now() - esperaDesde > UPDATE_SAMPLE_MAX_MS) { clearInterval(muestreo); return; }
      traceUpdateStep('esperando');
    }, UPDATE_SAMPLE_EVERY_MS);
    nuevo.postMessage({ type: 'SKIP_WAITING' });
    traceUpdateStep('skip_waiting');
  } else {
    // Fallback: contexto inseguro sin SW (LAN-HTTP típico de dev) o
    // detectamos cambio antes de que el SW esté en waiting. Reload
    // directo — el navegador pedirá HTML fresco al servidor. Aquí sí
    // damos margen al toast: sin SW no hay relevo que pueda pillarse a
    // medias, así que la espera no abre ninguna ventana de incoherencia.
    reload(1200);
  }
}

// Llamado tanto por checkForUpdate() como por el caller en Ajustes que
// quiera forzar el reload aunque haya bloqueos (no expuesto por defecto,
// solo si en el futuro queremos un "Forzar actualización"). Hoy nadie lo
// llama directamente: aplicamos solo cuando es seguro.
export function forceApplyNow() {
  if (!registration?.waiting) return;
  applyUpdate();
}

function markUpdateReady() {
  if (!hasUpdate) {
    hasUpdate = true;
    status = 'available';
    if (!promptTracked) {
      promptTracked = true;
      track({ name: 'app_update_prompt_shown' });
    }
    traceUpdateStep('detectada');
    emit();
  }
  void tryApplyIfReady();
}

function watchRegistration(reg: ServiceWorkerRegistration) {
  registration = reg;
  if (reg.waiting && navigator.serviceWorker.controller) {
    markUpdateReady();
  }
  reg.addEventListener('updatefound', () => {
    const installing = reg.installing;
    if (!installing) return;
    installing.addEventListener('statechange', () => {
      if (installing.state === 'installed' && navigator.serviceWorker.controller) {
        markUpdateReady();
      }
    });
  });
}

// Polling de /build-info.json: replica el comportamiento que tenía el
// antiguo UpdateBanner. Sirve como fallback cuando el navegador no
// dispara updatefound espontáneamente (Safari iOS sobre todo). Cuando
// detecta cambio de buildTime, fuerza un reg.update() y será el ciclo
// nativo el que dispare markUpdateReady().
async function pollBuildInfo(currentBuildTime: number) {
  try {
    const r = await fetch('/build-info.json?t=' + Date.now(), { cache: 'no-store' });
    if (!r.ok) return;
    const data = await r.json() as { buildTime: number };
    lastCheckAt = Date.now();
    if (data.buildTime !== currentBuildTime) {
      if ('serviceWorker' in navigator) {
        // Camino normal: pedir al navegador que refresque el SW; cuando
        // el nuevo SW pase a 'installed', statechange disparará
        // markUpdateReady().
        let reg = registration;
        if (!reg) {
          try {
            reg = await Promise.race([
              navigator.serviceWorker.ready,
              new Promise<never>((_, rej) => setTimeout(() => rej(new Error('timeout')), 5000)),
            ]);
            registration = reg;
          } catch { /* sin SW disponible */ }
        }
        if (reg) {
          await reg.update().catch(() => { /* offline / iOS */ });
        } else {
          // SW API existe pero no hay registración accesible — caemos al
          // fallback de reload directo.
          markBuildChangedFallback();
        }
      } else {
        // Contexto inseguro (LAN-HTTP típico de dev): navigator.serviceWorker
        // no existe. Sin SW posible, marcamos update y dejamos que
        // tryApplyIfReady haga reload directo cuando sea seguro.
        markBuildChangedFallback();
      }
    }
    emit();
  } catch { /* sin red */ }
}

function markBuildChangedFallback() {
  if (!hasUpdate) {
    hasUpdate = true;
    status = 'available';
    if (!promptTracked) {
      promptTracked = true;
      track({ name: 'app_update_prompt_shown' });
    }
    traceUpdateStep('detectada', { sinSW: true });
    emit();
  }
  void tryApplyIfReady();
}

// API pública para el botón "Buscar actualizaciones" en Ajustes.
// 'check-failed': la comprobación no respondió (red caída, fetch falló).
//   El caller debería ofrecer reload duro como fallback.
// 'has-update' / 'up-to-date': resultado normal.
//
// Funciona haya SW o no: el polling de build-info.json es la fuente de
// verdad para detectar cambios, y el SW solo se usa cuando está
// disponible para hacer la transición silenciosa.
export async function checkForUpdate(): Promise<'has-update' | 'up-to-date' | 'check-failed'> {
  // Válvula de escape del cortacircuitos: pulsar "Buscar actualizaciones"
  // es intención explícita del usuario → tanda nueva de intentos.
  clearApplyAttempts();
  // Si hay SW, intentamos refrescarlo (no bloqueante: si falla, seguimos).
  if ('serviceWorker' in navigator) {
    let reg: ServiceWorkerRegistration | null = registration;
    if (!reg) {
      try {
        reg = await Promise.race([
          navigator.serviceWorker.ready,
          new Promise<never>((_, rej) => setTimeout(() => rej(new Error('sw-ready-timeout')), 8000)),
        ]);
        registration = reg;
      } catch { /* sin SW disponible: seguimos con el polling */ }
    }
    if (reg) {
      try { await reg.update(); } catch { /* iOS / offline / cors */ }
    }
  }
  // Polling de build-info.json: única fuente de verdad confiable. Funciona
  // con o sin SW. Si la red está caída, el catch interno suprime el error
  // y lastCheckAt no se actualiza.
  const lastCheckBefore = lastCheckAt;
  await pollBuildInfo(__BUILD_TIME__);
  if (lastCheckAt === lastCheckBefore) {
    // El poll no consiguió actualizar el timestamp → fetch falló.
    return 'check-failed';
  }
  // Pequeño margen para que el ciclo updatefound → statechange='installed'
  // dispare markUpdateReady() antes de devolver al usuario.
  await new Promise((r) => setTimeout(r, 800));
  emit();
  return hasUpdate ? 'has-update' : 'up-to-date';
}

export function initUpdateManager() {
  if (initialized) return;
  initialized = true;
  reportPreviousTrace();

  // Si hay SW disponible, conectamos al ciclo de updatefound. Si no
  // (contexto inseguro tipo LAN-HTTP), seguimos adelante con el polling
  // para que el flujo de fallback funcione igualmente.
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then(watchRegistration).catch(() => { /* sin SW */ });

    // Recarga definitiva: si el SW nuevo toma el control minutos después
    // (típico: el viejo estaba drenando la cola offline y el skipWaiting
    // quedó en espera; el cortacircuitos ya paró las recargas a ciegas),
    // una única recarga nos deja en la versión nueva.
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      // Primera instalación (no había update detectada): no recargar.
      if (!hasUpdate) return;
      // Hay una aplicación en curso: su propio listener ya recarga.
      if (applying) return;
      // Edición en curso / flush del cliente: la versión nueva entrará en
      // el siguiente arranque.
      if (!isSafeToApply()) return;
      traceUpdateStep('relevo_tardio');
      window.location.reload();
    });
  }

  const currentBuildTime = __BUILD_TIME__;
  // Una comprobación inmediata al arrancar para que el timestamp en
  // Ajustes esté poblado desde el primer momento (en lugar de "aún sin
  // comprobar" hasta los 30s).
  void pollBuildInfo(currentBuildTime);
  setInterval(() => void pollBuildInfo(currentBuildTime), 30_000);

  // Reintentar aplicar al volver a primer plano (visibilitychange) y
  // cuando cambie el estado de la cola offline (pendingCount, isFlushing).
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void pollBuildInfo(currentBuildTime);
      void tryApplyIfReady();
    }
  });
  subscribeOfflineSync(() => { void tryApplyIfReady(); });
}
